import { useUIStore } from "@/stores/ui";

// Service-worker updates and the install prompt (X-2). main.tsx passes in
// vite-plugin-pwa's registerSW so this module stays testable without it.

type RegisterSW = (options: {
	immediate?: boolean;
	onNeedRefresh?: () => void;
	onOfflineReady?: () => void;
}) => (reloadPage?: boolean) => Promise<void>;

export function setupUpdates(registerSW: RegisterSW): void {
	let waiting = false;
	const updateSW = registerSW({
		immediate: true,
		// No popup (owner's call): the new version is applied silently once the tab is hidden,
		// so a reload never interrupts typing. Unsynced work is safe in IndexedDB.
		onNeedRefresh() {
			waiting = true;
			if (document.hidden) void updateSW(true);
		},
	});
	document.addEventListener("visibilitychange", () => {
		if (waiting && document.hidden) void updateSW(true);
	});
}

/** The browser's deferred install event (Chromium only; not in the DOM typings). */
export type InstallPromptEvent = Event & {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

/** Keeps the install prompt so Settings can offer "Install MOS" at a calm moment. */
export function listenForInstall(target: Window = window): () => void {
	const onPrompt = (e: Event) => {
		e.preventDefault();
		useUIStore.setState({ installPrompt: e as InstallPromptEvent });
	};
	const onInstalled = () => useUIStore.setState({ installPrompt: null });
	target.addEventListener("beforeinstallprompt", onPrompt);
	target.addEventListener("appinstalled", onInstalled);
	return () => {
		target.removeEventListener("beforeinstallprompt", onPrompt);
		target.removeEventListener("appinstalled", onInstalled);
	};
}
